import React from 'react';
import logoUrl from '../../assets/images/logos/court-letterhead.svg';

// On-screen twin of the Judicial Service of Ghana transcript template (see
// courtSummaryDoc.js / courtSummaryPdf.js): logo header, eight part banners with
// bordered tables, draft banner and certification blocks.
const partBannerClass =
  'bg-gray-200 border border-gray-400 px-3 py-1.5 text-[15px] font-bold text-gray-900 tracking-wide';

const cellClass = 'border border-gray-400 px-3 py-2 text-[13px] text-gray-900 align-top leading-relaxed';
const labelCellClass = `${cellClass} font-bold bg-gray-50 w-56`;

const LabelTable = ({ rows }) => (
  <table className="w-full border-collapse">
    <tbody>
      {rows.map(([label, value]) => (
        <tr key={label}>
          <td className={labelCellClass}>{label}</td>
          <td className={cellClass}>{value}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const HeadTable = ({ head, rows }) => (
  <table className="w-full border-collapse">
    <thead>
      <tr>
        {head.map((title) => (
          <th key={title} className={`${cellClass} bg-gray-200 font-bold text-left`}>
            {title}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((cells, i) => (
        <tr key={i}>
          {cells.map((cell, j) => (
            <td key={j} className={cellClass}>{cell}</td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

const Part = ({ title, children }) => (
  <section className="mt-6">
    <div className={partBannerClass}>{title}</div>
    <div className="pt-3 space-y-3">{children}</div>
  </section>
);

const SubHeading = ({ children }) => (
  <h4 className="text-sm font-bold text-gray-900 mt-2 mb-1">{children}</h4>
);

const Bullets = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1 text-[13px] text-gray-900 leading-relaxed">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const SignatureBlock = ({ title, statement, signerLabel }) => (
  <div className="border border-gray-400 p-4">
    <h5 className="text-sm font-bold text-gray-900 mb-2">{title}</h5>
    <p className="text-[13px] italic text-gray-700 leading-relaxed mb-4">{statement}</p>
    <div className="space-y-4 text-[13px] text-gray-900">
      <div>
        <span className="font-semibold">{signerLabel} — Name</span>
        <span className="border-b border-gray-500 ml-2 inline-block min-w-[280px] align-bottom">&nbsp;</span>
      </div>
      <div>
        <span className="font-semibold">Signature</span>
        <span className="border-b border-gray-500 ml-2 inline-block min-w-[280px] align-bottom">&nbsp;</span>
      </div>
      <div>
        <span className="font-semibold">Date</span>
        <span className="border-b border-gray-500 ml-2 inline-block min-w-[280px] align-bottom">&nbsp;</span>
      </div>
    </div>
  </div>
);

const CourtSummaryDocument = ({ model }) => {
  if (!model) return null;
  const { meta, part1, part2, part3, part4, part5, part6, part7, part8 } = model;

  return (
    <div className="bg-white text-gray-900 border border-gray-300 rounded shadow-sm">
      {/* Running header on subsequent printed pages is PDF-only; on screen the
          case identity repeats in the footer banner below. */}
      <div className="border-b-2 border-gray-800 mx-6 mt-6" />
      <header className="px-6 pt-5 pb-4 text-center">
        <img src={logoUrl} alt="Judicial Service of Ghana" className="h-16 mx-auto mb-3" />
        <h2 className="text-lg font-bold tracking-wide">JUDICIAL SERVICE OF GHANA</h2>
        <h3 className="text-[15px] font-bold mt-0.5">
          {(meta.courtName && meta.courtName !== 'Not stated'
            ? meta.courtName
            : 'Court of Appeal Division').toUpperCase()}
        </h3>
        <h3 className="text-[15px] font-bold mt-1">{meta.documentTitle}</h3>
        <p className="text-xs italic text-gray-600 mt-2 max-w-xl mx-auto leading-relaxed">
          {meta.bannerNotice}
        </p>
      </header>

      <main className="px-6 pb-4">
        <Part title="PART 1 — CASE DETAILS">
          <LabelTable rows={part1.rows} />
        </Part>

        <Part title="PART 2 — PARTICIPANTS">
          <HeadTable
            head={part2.head}
            rows={part2.rows.map((p) => [p.role, p.name, p.notes])}
          />
        </Part>

        <Part title="PART 3 — TRANSCRIPT GENERATION DETAILS">
          <LabelTable rows={part3.rows} />
          <p className="text-xs italic text-gray-600 leading-relaxed">{part3.disclaimer}</p>
        </Part>

        <Part title="PART 4 — SUMMARY">
          <div>
            <SubHeading>Purpose</SubHeading>
            <p className="text-[13px] leading-relaxed">{part4.purpose}</p>
          </div>
          <div>
            <SubHeading>Summary of Proceedings</SubHeading>
            <p className="text-[13px] leading-relaxed">{part4.narrative}</p>
          </div>
          {part4.keyPoints.length > 0 && (
            <div>
              <SubHeading>Key Points</SubHeading>
              <Bullets items={part4.keyPoints} />
            </div>
          )}
          <div>
            <SubHeading>Decisions and Orders</SubHeading>
            <Bullets items={part4.decisions} />
          </div>
          {part4.actionItems.length > 0 && (
            <div>
              <SubHeading>Action Items</SubHeading>
              <Bullets items={part4.actionItems} />
            </div>
          )}
          <div>
            <SubHeading>Next Date</SubHeading>
            <p className="text-[13px] leading-relaxed">{part4.nextDate}</p>
          </div>
          {part4.openQuestions.length > 0 && (
            <div>
              <SubHeading>Open Questions</SubHeading>
              <Bullets items={part4.openQuestions} />
            </div>
          )}
        </Part>

        <Part title="PART 5 — TRANSCRIPT">
          <p className="text-xs italic text-gray-600 leading-relaxed">{part5.note}</p>
          <HeadTable
            head={part5.head}
            rows={part5.rows.map((r) => [r.time, r.speaker, r.proceedings])}
          />
          <p className="text-xs italic text-gray-600 leading-relaxed">{part5.conventions}</p>
        </Part>

        <Part title="PART 6 — REVIEW AND CORRECTIONS LOG">
          <p className="text-xs italic text-gray-600 leading-relaxed">{part6.intro}</p>
          {part6.rows.length > 0 ? (
            <>
              <HeadTable
                head={part6.head}
                rows={part6.rows.map((r) => [String(r.item), r.correction, r.reason, r.initials])}
              />
              <div>
                <SubHeading>Items flagged by the AI system for checking against the recording</SubHeading>
                <Bullets items={part6.rows.map((r) => r.correction)} />
              </div>
            </>
          ) : (
            <p className="text-[13px]">No corrections or flags recorded against this draft yet.</p>
          )}
          <div>
            <SubHeading>Reviewer notes</SubHeading>
            <Bullets items={part6.reviewerNotes} />
          </div>
        </Part>

        <Part title="PART 7 — VERIFICATION AND CERTIFICATION">
          <p className="text-xs italic text-gray-600 leading-relaxed">{part7.intro}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <SignatureBlock
              title="A.  Verification by the Court Registrar"
              statement={part7.registrarStatement}
              signerLabel="Court Registrar"
            />
            <SignatureBlock
              title="B.  Approval by the Presiding Judge"
              statement={part7.judgeStatement}
              signerLabel="Presiding Judge"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="border border-dashed border-gray-400 p-4 text-center text-xs text-gray-500 min-h-[80px] flex items-center justify-center">
              OFFICIAL COURT STAMP / SEAL — affix on certification
            </div>
            <div className="border border-dashed border-gray-400 p-4 text-center text-xs text-gray-500 min-h-[80px] flex items-center justify-center">
              CERTIFICATION MARK / SERIAL NO. — enter on certification
            </div>
          </div>
        </Part>

        <Part title="PART 8 — DISTRIBUTION AND HANDLING">
          <LabelTable rows={part8.rows} />
        </Part>
      </main>

      <footer className="border-t border-gray-400 mx-6 mb-5 py-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-600">
        <span className="font-bold text-gray-900">{meta.draftLabel}</span>
        <span>Transcript ID: {meta.transcriptId}</span>
        <span>{meta.caseTitle} — Suit No.: {meta.suitNumber}</span>
      </footer>
    </div>
  );
};

export default CourtSummaryDocument;
